import React, { useEffect, useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stack,
  InputAdornment,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { Item, AddStockRequest, InventoryBatch } from '../../types';
import { PriceEntryMode } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { errorMessage } from '../../utils/errorMessage';
import { tracksBatches } from '../../utils/inventoryMode';
import { getPriceForEntryMode } from '../../utils/pricing';

interface AddStockDialogProps {
  open: boolean;
  onClose: () => void;
  item: Item;
  onSuccess: () => void | Promise<void>;
  batchTrackingEnabled?: boolean;
}

const AddStockDialog: React.FC<AddStockDialogProps> = ({
  open,
  onClose,
  item,
  onSuccess,
  batchTrackingEnabled,
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batches, setBatches] = useState<InventoryBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const canUseBatchTracking = batchTrackingEnabled !== false;

  const [formData, setFormData] = useState<AddStockRequest>({
    quantity: 1,
    unitPrice: getPriceForEntryMode(item.price, item.priceGross, item.priceEntryMode),
    batchId: undefined,
    batchNumber: '',
    productionDate: '',
    expirationDate: '',
    notes: '',
  });

  useEffect(() => {
    if (!open || !canUseBatchTracking || !tracksBatches(item)) {
      setBatches([]);
      return;
    }

    let active = true;
    setBatchesLoading(true);
    apiClient.getInventoryBatches(item.id)
      .then(data => {
        if (active) setBatches(data);
      })
      .catch((err: unknown) => {
        if (active) setError(errorMessage(err, 'Failed to load batches'));
      })
      .finally(() => {
        if (active) setBatchesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canUseBatchTracking, open, item]);

  useEffect(() => {
    if (!open) return;
    setFormData({
      quantity: 1,
      unitPrice: getPriceForEntryMode(item.price, item.priceGross, item.priceEntryMode),
      batchId: undefined,
      batchNumber: '',
      productionDate: '',
      expirationDate: '',
      notes: '',
    });
    setError(null);
  }, [open, item.id, item.price, item.priceGross, item.priceEntryMode]);

  const handleInputChange = (field: keyof AddStockRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'quantity' ? (value === '' ? '' : Number(value)) : value,
    }));
  };

  const handleBatchChange = (event: SelectChangeEvent<string>) => {
    const batchId = event.target.value === '__new__' ? undefined : event.target.value;
    const selectedBatch = batchId ? batches.find(batch => batch.id === batchId) : undefined;

    setFormData(prev => ({
      ...prev,
      batchId,
      batchNumber: selectedBatch?.batchNumber || '',
      unitPrice: getPriceForEntryMode(
        selectedBatch?.price,
        selectedBatch?.priceGross,
        selectedBatch?.priceEntryMode ?? item.priceEntryMode,
      ) ?? getPriceForEntryMode(item.price, item.priceGross, item.priceEntryMode),
      productionDate: selectedBatch?.productionDate || '',
      expirationDate: selectedBatch?.expirationDate || '',
      notes: selectedBatch?.notes || '',
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!Number.isInteger(Number(formData.quantity)) || Number(formData.quantity) <= 0) {
      setError(t('inventory.quantity'));
      setLoading(false);
      return;
    }
    if (canUseBatchTracking && tracksBatches(item) && !formData.batchId && !String(formData.batchNumber || '').trim()) {
      setError(t('inventory.batchNumberRequired'));
      setLoading(false);
      return;
    }
    if (canUseBatchTracking && tracksBatches(item) && !formData.batchId && formData.productionDate && formData.expirationDate && formData.expirationDate < formData.productionDate) {
      setError(t('inventory.expirationDate'));
      setLoading(false);
      return;
    }

    try {
      const request: AddStockRequest = {
        quantity: Number(formData.quantity),
      };

      // Add optional fields only if batch tracking is enabled and they have values
      if (canUseBatchTracking && tracksBatches(item)) {
        if (formData.batchId) {
          request.batchId = formData.batchId;
        } else {
          request.unitPrice = Number(formData.unitPrice);
          if (formData.batchNumber) request.batchNumber = formData.batchNumber;
          if (formData.productionDate) request.productionDate = formData.productionDate;
          if (formData.expirationDate) request.expirationDate = formData.expirationDate;
          if (formData.notes) request.notes = formData.notes;
        }
      }

      const response = await apiClient.addStock(item.id, request);
      
      if (response.success) {
        // Wait for the parent to refresh the item and inventory projections
        // before closing. This prevents the dialog from briefly showing the
        // pre-mutation price/stock snapshot.
        await onSuccess();
        onClose();
        // Reset form
        setFormData({
          quantity: 1,
          unitPrice: getPriceForEntryMode(item.price, item.priceGross, item.priceEntryMode),
          batchId: undefined,
          batchNumber: '',
          productionDate: '',
          expirationDate: '',
          notes: '',
        });
      } else {
        setError(response.message);
      }
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to add stock'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
    }
  };

  const isNewTrackedBatch = canUseBatchTracking && tracksBatches(item) && !formData.batchId;
  const isFormValid = Number.isInteger(Number(formData.quantity)) && Number(formData.quantity) > 0
    && (!isNewTrackedBatch
      || (String(formData.batchNumber || '').trim().length > 0 && Number.isFinite(Number(formData.unitPrice)) && Number(formData.unitPrice) >= 0
        && (!formData.productionDate || !formData.expirationDate || formData.expirationDate >= formData.productionDate)));
  const existingBatchSelected = Boolean(formData.batchId);
  const priceEntryModeText = item.priceEntryMode === PriceEntryMode.VAT_INCLUDED
    ? t('inventory.priceEntryModeVatIncluded')
    : t('inventory.priceEntryModeVatExcluded');

  return (
    <AppDialog presentation="compact" open={open} onClose={handleClose} closeDisabled={loading} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('inventory.addStock')}</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{item.name}</Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            {/* Basic Fields */}
            <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                label={t('inventory.quantity')}
                type="number"
                value={formData.quantity}
                onChange={handleInputChange('quantity')}
                fullWidth
                required
                inputProps={{ min: 1, step: 1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('inventory.units')}</InputAdornment>,
                }}
                sx={{ mt: 2 }}
              />

            </Box>

            {/* Advanced Fields - Only show when batch tracking is enabled */}
            <Collapse in={canUseBatchTracking && tracksBatches(item)}>
              <Stack spacing={2}>
                <FormControl fullWidth required={canUseBatchTracking && tracksBatches(item)}>
                  <InputLabel id="add-stock-batch-label">{t('inventory.batchSelection')}</InputLabel>
                  <Select
                    labelId="add-stock-batch-label"
                    value={formData.batchId || '__new__'}
                    label={t('inventory.batchSelection')}
                    onChange={handleBatchChange}
                    disabled={batchesLoading}
                  >
                    <MenuItem value="__new__">{t('inventory.newBatch')}</MenuItem>
                    {batches.map(batch => (
                      <MenuItem key={batch.id} value={batch.id}>
                        {batch.batchNumber || batch.id.slice(0, 8)} ({batch.remainingQuantity} {t('inventory.units')})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label={t('inventory.batchSalePrice')}
                  type="number"
                  value={formData.unitPrice ?? ''}
                  onChange={handleInputChange('unitPrice')}
                  fullWidth
                  required={!existingBatchSelected}
                  disabled={existingBatchSelected}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">TRY</InputAdornment>,
                  }}
                  helperText={existingBatchSelected
                    ? t('inventory.existingBatchPriceHelp')
                    : `${t('inventory.newBatchPriceHelp')} ${t('inventory.priceEntryModeHelp').replace('{mode}', priceEntryModeText)}`}
                />

                <TextField
                  label={t('inventory.batchNumber')}
                  value={formData.batchNumber}
                  onChange={handleInputChange('batchNumber')}
                  fullWidth
                  required={canUseBatchTracking && tracksBatches(item) && !existingBatchSelected}
                  disabled={!canUseBatchTracking || !tracksBatches(item) || existingBatchSelected}
                  error={canUseBatchTracking && tracksBatches(item) && !existingBatchSelected && !String(formData.batchNumber || '').trim()}
                  placeholder={t('inventory.batchNumberPlaceholder')}
                />

                <Box display="flex" gap={2}>
                  <TextField
                    label={t('inventory.productionDate')}
                    type="date"
                    value={formData.productionDate}
                    onChange={handleInputChange('productionDate')}
                    fullWidth
                    disabled={existingBatchSelected}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    label={t('inventory.expirationDate')}
                    type="date"
                    value={formData.expirationDate}
                    onChange={handleInputChange('expirationDate')}
                    fullWidth
                    disabled={existingBatchSelected}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: formData.productionDate || undefined,
                    }}
                  />
                </Box>

                <TextField
                  label={t('inventory.notes')}
                  value={formData.notes}
                  onChange={handleInputChange('notes')}
                  fullWidth
                  disabled={existingBatchSelected}
                  multiline
                  rows={2}
                  placeholder={t('inventory.notesPlaceholder')}
                />
              </Stack>
            </Collapse>

            {/* Summary */}
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('inventory.operationPreview')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.addingSummary')}: {formData.quantity} {t('inventory.units')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.newTotal')}: {item.totalStockQuantity + Number(formData.quantity)} {t('inventory.units')}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={loading || !isFormValid}
            startIcon={<AddIcon />}
          >
            {loading ? t('common.adding') : t('inventory.addStock')}
          </Button>
        </DialogActions>
      </form>
    </AppDialog>
  );
};

export default AddStockDialog;
